import { useState } from "react";
import styles from "../../pages/BlogArticle.module.scss";
import { useLanguage } from "../../contexts/LanguageContext";
import { blogText } from "../../i18n/blog";

const isInstagramUrl = (value) => {
  if (!value || typeof value !== "string") return false;

  return /^https?:\/\/(www\.)?instagram\.com\/(p|reel|tv)\//.test(value);
};

const getEmbedSrc = (url) => {
  if (!url || typeof url !== "string") return "";

  const match = url.match(/instagram\.com\/(p|reel|tv)\/([^/?]+)/);
  if (!match) return "";

  return `https://www.instagram.com/${match[1]}/${match[2]}/embed`;
};

export const getInstagramUrl = (item) => {
  if (!item) return "";

  if (typeof item === "string") {
    return isInstagramUrl(item) ? item : "";
  }

  return (
    item.instagramEmbedUrl || item.instagramPostUrl || item.instagramUrl || ""
  );
};

export const getImageUrl = (item) => {
  if (!item) return "";
  if (typeof item === "string") {
    return isInstagramUrl(item) ? "" : item;
  }

  return item.image || item.src || item.url || "";
};

export const getVideoUrl = (item) => {
  if (!item) return "";

  if (typeof item === "string") {
    return /\.(mp4|webm|mov)(\?.*)?$/i.test(item) ? item : "";
  }

  return item.video || item.videoUrl || "";
};

export function InstagramEmbed({ url, caption = "Instagram post" }) {
  const { lang } = useLanguage();
  const t = blogText[lang] || blogText.JP;
  const [isLoaded, setIsLoaded] = useState(false);
  const embedSrc = getEmbedSrc(url);

  if (!url || !embedSrc) return null;

  return (
    <div className={styles.instagramEmbedWrap}>
      {!isLoaded && (
        <div className={styles.instagramLoader} aria-live="polite">
          <span />
          <strong>{t.instagramLoading}</strong>
        </div>
      )}

      <iframe
        key={embedSrc}
        src={embedSrc}
        title={caption}
        loading="lazy"
        scrolling="no"
        allowTransparency="true"
        onLoad={() => setIsLoaded(true)}
        style={{
          width: "100%",
          minHeight: 430,
          border: "none",
          display: "block",
        }}
      />
    </div>
  );
}

export function BlogMedia({ media, alt, className = "" }) {
  const instagramUrl = getInstagramUrl(media);
  const videoUrl = getVideoUrl(media);
  const imageUrl = getImageUrl(media);

  if (instagramUrl) {
    return <InstagramEmbed url={instagramUrl} caption={alt} />;
  }

  if (videoUrl) {
    return (
      <video
        className={className}
        src={videoUrl}
        controls
        playsInline
        preload="metadata"
      />
    );
  }

  if (!imageUrl) return null;

  return <img className={className} src={imageUrl} alt={alt} />;
}
